import '../styles/pdf-pages.css';
import '../styles/columnar-table.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateTourCo2 } from '@/domain/impact-calculate-tour/entities/calculate-tour-co2.entity';
import { CalculateTourEnv } from '@/domain/impact-calculate-tour/entities/calculate-tour-env.entity';
import { CalculateTourEss } from '@/domain/impact-calculate-tour/entities/calculate-tour-ess.entity';
import {
  selectImpactCalculateTourCo2ForProject,
  selectImpactCalculateTourEnvForProject,
  selectImpactCalculateTourEssForProject,
} from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import { selectProjectTourOnTourSequences } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { GaugeScore } from '@/ui/components/score-bars/GaugeScore';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

import { formatProjectDatesTz } from '../../../utils/format-project-dates';
import CalendarMonthSvg from '../assets/calendar_month.svg';
import FairlyBackgroundLogo from '../assets/logo-fairly-clear-background.png';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import {
  displayDecimal,
  formatTopicScore,
} from '../utils/display-scores.utils';

export function PdfTourPage1({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const tour = useAppSelector(
    selectCurrentProjectTour,
  ) satisfies ProjectTour | null;
  const tourId = tour?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateTourCo2ForProject(tourId),
  ) satisfies CalculateTourCo2;

  const impactEnv = useAppSelector(
    selectImpactCalculateTourEnvForProject(tourId),
  ) satisfies CalculateTourEnv;

  const impactEss = useAppSelector(
    selectImpactCalculateTourEssForProject(tourId),
  ) satisfies CalculateTourEss;

  const sequences = useAppSelector(selectProjectTourOnTourSequences);

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!tour) {
    return <h1>{translate('PdfExports.tour-not-found')}</h1>;
  }

  const impactEnvScore100 =
    !impactEnv.general.score || !impactEnv.general.maxScore
      ? 0
      : Math.round(
          (impactEnv.general.score * 100) / impactEnv.general.maxScore,
        );

  const impactEssScore100 =
    !impactEss.general.score || !impactEss.general.maxScore
      ? 0
      : Math.round(
          (impactEss.general.score * 100) / impactEss.general.maxScore,
        );

  return (
    <div id="page1" className="pdf-page">
      <div className="content">
        <div className="pdf-header">
          <img
            className="tour-logo"
            src={tour.image || FairlyBackgroundLogo}
            alt={translate('PdfExports.tour-logo')}
          />
          <div>
            <h1>{tour.name}</h1>
            {tour.startDate && tour.endDate && (
              <div className="header-subtitles">
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="tour-date">
                  {formatProjectDatesTz(tour.startDate, tour.endDate)}
                </span>
              </div>
            )}
            <div className="pills">
              <span className="tag-pill pill-list">
                {translate('PdfExports.tour')}
              </span>
            </div>
          </div>
        </div>

        <div className="pdf-separator"></div>

        <div className="columnar-table">
          <h2>{translate('PdfExports.columnar-table')}</h2>
          <div className="columnar-table-scores">
            <div className="score-item" id="overview-co2">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard type="co2" scoreLetter="-" />
                  {translate('Projects.co2')}
                </h3>
              </div>
              <div className="score-item-value">
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-emissions')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactCo2.global.score)}
                      </span>{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li className="score-highlight-secondary">
                    <span>
                      {translate('ProjectsTour.project-tour-general')}
                    </span>
                    <span>
                      {displayDecimal(impactCo2.general.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Sections.CREATION',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        impactCo2.general.sections.CREATION.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Sections.MANAGEMENT',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        impactCo2.general.sections.MANAGEMENT.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate(
                        'ProjectsTour.GeneralCo2Quiz.Sections.COMMUNICATION',
                      )}
                    </span>
                    <span>
                      {displayDecimal(
                        impactCo2.general.sections.COMMUNICATION.score,
                      )}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li className="score-highlight-secondary">
                    <span>{translate('ProjectsTour.project-tour-ontour')}</span>
                    <span>
                      {displayDecimal(impactCo2.ontour.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  {sequences.map((sequence) => (
                    <li key={sequence.id}>
                      <span>{sequence.name}</span>
                      <span>
                        {displayDecimal(
                          impactCo2.ontour.bySequence[sequence.id]?.score ?? 0,
                        )}{' '}
                        {translate('Projects.teCO2')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-env">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="env"
                    scoreLetter={impactEnv.general.scoreLetter}
                  />
                  {translate('Projects.env')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    textColor="black"
                    score={impactEnvScore100}
                    maxScore={100}
                    bgColor="#DDDDDD99"
                    overrideColor={
                      getColorForScore(impactEnv.general.scoreLetter).main
                    }
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEnv.general.score)}
                      </span>
                      /{impactEnv.general.maxScore}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Env.Topics.EQUIPMENT_AND_SCENOGRAPHY')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEnv.general.topics.EQUIPMENT_AND_SCENOGRAPHY,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.FOOD_BEVERAGES')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.general.topics.FOOD_BEVERAGES,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.RESOURCES_POLLUTION')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.general.topics.RESOURCES_POLLUTION,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.COMMUNICATION')}</span>
                    <span>
                      {formatTopicScore(impactEnv.general.topics.COMMUNICATION)}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.general.topics.PUBLIC_ENGAGEMENT,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-ess">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="ess"
                    scoreLetter={impactEss.general.scoreLetter}
                  />
                  {translate('Projects.ess')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    score={impactEssScore100}
                    maxScore={100}
                    textColor="black"
                    bgColor="#DDDDDD99"
                    overrideColor={
                      getColorForScore(impactEss.general.scoreLetter).main
                    }
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEss.general.score)}
                      </span>
                      /{impactEss.general.maxScore}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Ess.Topics.PARITY_EQUALITY')}</span>
                    <span>
                      {formatTopicScore(
                        impactEss.general.topics.PARITY_EQUALITY,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.general.topics.ACCESSIBILITY_SOCIAL_DIALOGUE,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.general.topics.LOCAL_IMPACT_RSE_ENGAGEMENT,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="navigation-controls">
          <a href="#page2" className="tag-pill">
            {translate('PdfExports.go-to-score-details')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={1} total={totalPages} />
    </div>
  );
}
